'use client'
// this component should remain client side to ensure
// the modal loads as fast as possible. It still renders SSR,
// if its loaded from a fresh page reload.

import { SignInAppleButton } from '@/components/sign-in-apple-button'
import { SignInGoogleButton } from '@/components/sign-in-google-button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Link } from '@/components/ui/deprecated-link'
import { EMAIL_FIELD_NAME } from '@/modules/auth/constants'
import { SignInFields } from '@/modules/auth/signin-fields'
import { MainRoutes } from '@/utils/routes'
import Image from 'next/image'
import { signInAction } from '@/modules/auth/signin-action'
import {
  SalaryForceLoginType,
  useSalaryTableStore,
} from '@/modules/salary/salary-table-context'
import {
  useG2GTracking,
  getFilterLabel,
} from '@/modules/salary/analytics/use-g2g-tracking'
import { useSalaryFilters } from '@/modules/salary/filters/use-salary-filters'
import { useEffect } from 'react'

export function SalaryLoginModal({
  isAuthenticated,
  ipAddress,
}: {
  isAuthenticated: boolean
  ipAddress: string | null
}) {
  const setSalaryForceLoginType = useSalaryTableStore(
    (state) => state.setSalaryForceLoginType,
  )
  const salaryForceLoginType = useSalaryTableStore(
    (state) => state.salaryForceLoginType,
  )

  const isOpen = !isAuthenticated && Boolean(salaryForceLoginType)
  const isPaginationForceType =
    salaryForceLoginType === SalaryForceLoginType.Pagination
  const { trackG2GView } = useG2GTracking()
  const { salaryFilterControls } = useSalaryFilters({
    isAuthenticated: false,
  })

  useEffect(() => {
    if (isOpen) {
      trackG2GView({
        professionName: getFilterLabel(salaryFilterControls?.profession),
        specialtyName: getFilterLabel(salaryFilterControls?.specialty),
        locationName: getFilterLabel(salaryFilterControls?.state?.label),
        moduleName: 'modal',
        modalTrigger: 'salary-search',
      })
    }
  }, [isOpen, salaryFilterControls, trackG2GView])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => {
        setSalaryForceLoginType(null)
      }}
    >
      <DialogContent title="Ready to see all salaries?">
        <DialogTitle className="text-2xl">
          {isPaginationForceType
            ? 'Continue viewing salaries'
            : 'Ready to see all salaries?'}
        </DialogTitle>
        <p className="text-copy-accent mb-2">
          {isPaginationForceType
            ? 'We use a “give-to-get” model - add your anonymous salary to unlock access to all the salaries ' +
              'shared by your peers.'
            : 'See if you’re fairly paid and help us bring more transparency to medicine - it only takes a ' +
              'minute to unlock all salaries.'}
        </p>
        <p className="text-copy-accent text-xs">
          By continuing, you agree to our{' '}
          <Link href={MainRoutes.Terms} className="text-xs font-normal">
            Terms of Use
          </Link>{' '}
          and{' '}
          <Link href={MainRoutes.PrivacyPolicy} className="text-xs font-normal">
            Privacy Policy
          </Link>
        </p>

        <SignInGoogleButton className="font-semibold mb-0" />
        <SignInAppleButton className="font-semibold" />

        <div className="flex items-center justify-center w-full">
          <div className="w-full h-px bg-background-divider" />
          <span className="px-4 text-copy-accent font-medium text-sm">OR</span>
          <div className="w-full h-px bg-background-divider" />
        </div>

        <form
          action={async (formData) => {
            const email = formData.get(EMAIL_FIELD_NAME) as string
            await signInAction({ email, ipAddress })
          }}
        >
          <SignInFields title="Sign in with email" />
        </form>

        <div className="flex flex-col items-center">
          <Image
            src="/assets/illustrations/built-by.png"
            alt=""
            width={152}
            height={40}
            quality={100}
          />
          <p className="text-center mt-1">
            <span className="font-medium text-copy-primary block text-sm">
              Made by clinicians for clinicians
            </span>
            <span className="text-xs text-copy-muted">
              Loved by 10K+ physicians, APPs and students
            </span>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
